import React, { useState } from 'react';
import { View, Text, ScrollView, TextInput, TouchableOpacity, RefreshControl, StyleSheet } from 'react-native';
import { Header } from '../../components/Header';
import { StatusBadge } from '../../components/StatusBadge';
import { useGetMeetingsQuery } from '../../store/api';
import { useTheme } from '../../theme/ThemeContext';
import { MeetingStatus } from '../../types';
import { COLORS, getThemeStyles, globalStyles } from '../../theme/designSystem';

export const MeetingHistoryScreen = ({ navigation }: any) => {
  const [selectedStatus, setSelectedStatus] = useState<MeetingStatus | 'ALL'>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const { data: meetings = [], isLoading, refetch } = useGetMeetingsQuery();
  const { isDark } = useTheme();
  const theme = getThemeStyles(isDark);

  const filteredMeetings = meetings.filter((m) => {
    const matchesStatus = selectedStatus === 'ALL' || m.status === selectedStatus;
    const matchesSearch =
      m.visitorName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (m.visitorCompany && m.visitorCompany.toLowerCase().includes(searchQuery.toLowerCase())) ||
      m.visitorEmail.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.visitorPhone.includes(searchQuery);
    return matchesStatus && matchesSearch;
  });

  return (
    <View style={[globalStyles.container, { backgroundColor: theme.bg }]}>
      <Header title="Meeting History" subtitle="Track status of all submitted meeting requests" />

      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ paddingHorizontal: 20, paddingBottom: 24 }}
        refreshControl={<RefreshControl refreshing={isLoading} onRefresh={refetch} />}
      >
        {/* Search Bar */}
        <View
          style={[
            globalStyles.card,
            {
              backgroundColor: theme.cardBg,
              borderColor: theme.border,
              padding: 12,
              marginBottom: 14,
              flexDirection: 'row',
              alignItems: 'center',
            },
          ]}
        >
          <Text style={{ fontSize: 16, marginRight: 8 }}>🔍</Text>
          <TextInput
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholder="Search visitor name, company, phone..."
            placeholderTextColor={theme.textSecondary}
            style={{ flex: 1, fontSize: 13, color: theme.textPrimary }}
          />
        </View>

        {/* Filter Pills */}
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 16 }}>
          {['ALL', MeetingStatus.PENDING, MeetingStatus.CONFIRMED, MeetingStatus.REJECTED].map(
            (status) => {
              const isSelected = selectedStatus === status;
              return (
                <TouchableOpacity
                  key={status}
                  onPress={() => setSelectedStatus(status as any)}
                  style={[
                    styles.filterPill,
                    {
                      backgroundColor: isSelected ? COLORS.primary : theme.cardBg,
                      borderColor: isSelected ? COLORS.primary : theme.border,
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.filterText,
                      { color: isSelected ? '#FFFFFF' : theme.textPrimary },
                    ]}
                  >
                    {status}
                  </Text>
                </TouchableOpacity>
              );
            },
          )}
        </ScrollView>

        {/* Meetings List */}
        {filteredMeetings.length === 0 ? (
          <View style={[globalStyles.card, { backgroundColor: theme.cardBg, borderColor: theme.border, alignItems: 'center', marginVertical: 16 }]}>
            <Text style={{ fontSize: 32, marginBottom: 8 }}>📁</Text>
            <Text style={[styles.emptyTitle, { color: theme.textPrimary }]}>
              No meetings found matching your filter
            </Text>
          </View>
        ) : (
          filteredMeetings.map((meeting) => (
            <TouchableOpacity
              key={meeting.id}
              onPress={() => navigation.navigate('MeetingDetails', { id: meeting.id })}
              style={[styles.meetingCard, { backgroundColor: theme.cardBg, borderColor: theme.border }]}
            >
              <View style={{ flex: 1, marginRight: 10 }}>
                <Text style={[styles.visitorName, { color: theme.textPrimary }]}>
                  {meeting.visitorName}
                </Text>
                {meeting.visitorCompany && (
                  <Text style={[styles.companyName, { color: theme.textSecondary }]}>
                    {meeting.visitorCompany}
                  </Text>
                )}
                <Text style={[styles.slotText, { color: theme.textSecondary }]}>
                  📅 {meeting.meetingDate} at <Text style={{ color: COLORS.primary, fontWeight: '700' }}>{meeting.meetingTime}</Text>
                </Text>
                <Text style={[styles.purposeText, { color: theme.textSecondary }]} numberOfLines={1}>
                  {meeting.meetingPurpose}
                </Text>
              </View>

              <StatusBadge status={meeting.status} />
            </TouchableOpacity>
          ))
        )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  filterPill: {
    marginRight: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 12,
    borderWidth: 1,
  },
  filterText: {
    fontSize: 11,
    fontWeight: '800',
  },
  emptyTitle: {
    fontSize: 14,
    fontWeight: '700',
  },
  meetingCard: {
    padding: 16,
    borderRadius: 16,
    borderWidth: 1,
    marginBottom: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  visitorName: {
    fontSize: 15,
    fontWeight: '800',
  },
  companyName: {
    fontSize: 11,
    marginBottom: 4,
  },
  slotText: {
    fontSize: 11,
  },
  purposeText: {
    fontSize: 11,
    marginTop: 2,
  },
});
